// Layer-2 playtest hooks (docs/DEV_PLAYTEST.md): deterministic autoplay, frame
// freezing and a polling handle for the visual harness.
//
// Everything here is behind the `__BEATME_DEBUG__` build flag. A normal
// `vite build` folds the flag to `false`, which turns every call site into a
// dead branch — rollup then drops this module (and the bot/track code it pulls
// out of @beatme/sim) entirely. That is also why all of the state lives on one
// `Playtest` object rather than on `Session`: class *members* survive
// minification even when unreachable, so a shipped `Session` must not declare
// any. Grep the built bundle for `autoplay` — it is not there.
//
//   BEATME_DEBUG=1 npm run build -w @beatme/client   → debug-capable build
//   npm run build -w @beatme/client                  → stripped
//   npm run dev                                      → always debug-capable
//
// Params (all optional, all ignored unless the flag is on):
//   ?autoplay=<perfect|expert|good|average|sloppy>  bot plays instead of you
//   &botseed=<n>     error-roll seed for the bot (default 1)
//   &pauseAt=<frame> freeze the loop when the player sim reaches that frame
//   &timescale=<x>   integer sim steps per real frame (long watches)
//   &result=<0|1>    autoplay death opens the result screen instead of freezing
//   &theme=<name>    palette override (render.ts PALETTES)
//   &tutorial=1      force the stop-frame tutorial on regardless of localStorage
//                    (autoplay still bypasses it — bot runs must not stop)
//   &ladder=1        force the rival ladder on regardless of the server's
//                    LADDER_PCT (§4bis.21) — the only way to see it while the
//                    flag is dark, and how the ladder-result shot is captured
//   &peak=1          force the peak-moment arm on regardless of the server's
//                    PEAK_PCT (§4bis.22) — the `&ladder=1` pattern
//   &peakui=1        autoplay mounts the seal overlay on a peak death (mirrors
//                    &result=1; without it a bot run never shows the seal, so
//                    every pre-peak baseline stays byte-identical)
//   &fair=1          force honest silhouettes on regardless of the server's
//                    FAIR_PCT (§4bis.23) — the `&peak=1` pattern
//   &duel=1          force the blitz-duel arm on regardless of the server's
//                    DUEL_PCT (§4bis.28) — the `&fair=1` pattern. The SLICE is
//                    still server-derived, so this only works on a link whose
//                    run is long enough to mint one
//   &sealhold=1      hold the §4bis.30 mid-run 活过了 stamp at full size instead
//                    of letting it fade after 1.4 s (it plays WHILE the run
//                    continues, so a late capture would find nothing).
//   &duelhold=1      hold the duel countdown on its first beat, so the frozen
//                    handover frame has a deterministic shot (§4bis.28)
//
// `window.__beatme` also carries `renderSfx(name)` (offline SFX render, no URL
// param needed) — see `exposeAudioDebug` at the bottom.

import {
  botInputs,
  generateTrack,
  truncateAtDeath,
  type Action,
  type BotConfig,
  type InputEvent,
} from '@beatme/sim';

import { SFX_NAMES, renderSfx, type RenderedSfx, type Sfx } from './game/audio';
import { el } from './ui/dom';

export type BotTier = 'perfect' | 'expert' | 'good' | 'average' | 'sloppy';

/** Mirrors `SKILLS` in packages/sim/scripts/validate-week.ts — same tiers, same
 *  numbers, so a shot and a Layer-1 report describe the same player. */
export const BOT_TIERS: Record<BotTier, BotConfig> = {
  perfect: { reaction: 0, errorPermille: 0 },
  expert: { reaction: 1, errorPermille: 30 },
  good: { reaction: 2, errorPermille: 100 },
  average: { reaction: 3, errorPermille: 220 },
  sloppy: { reaction: 3, errorPermille: 450 },
};

const TIER_NAMES = Object.keys(BOT_TIERS) as BotTier[];

/** Placeholder identity on an autoplay result screen — nothing is submitted, so
 *  there is no server nickname to show. */
export const DEMO_NICKNAME = 'DEMO';

export interface DebugOptions {
  autoplay: BotTier | null;
  botSeed: number;
  pauseAt: number | null;
  timescale: number;
  /** `&result=1`: show the (local-only) result screen instead of freezing. */
  showResult: boolean;
  theme: string | null;
  /** `&tutorial=1`: pretend this is a fresh device so the stop frames fire. */
  tutorial: boolean;
  /** `&ladder=1`: pretend this device won the `LADDER_PCT` draw (§4bis.21). */
  ladder: boolean;
  /** `&peak=1`: pretend this device won the `PEAK_PCT` draw (§4bis.22). */
  peak: boolean;
  /** `&peakui=1`: autoplay may mount the seal overlay (mirrors `showResult`). */
  showPeak: boolean;
  /** `&fair=1`: pretend this device won the `FAIR_PCT` draw (§4bis.23). */
  fair: boolean;
  /** `&duel=1`: pretend this device won the `DUEL_PCT` draw (§4bis.28). The
   *  SLICE still has to exist — only the server can derive one — so this forces
   *  the arm, never the feature. */
  duel: boolean;
  /**
   * `&handoffmint=1`: pretend the SERVER answered `handoffMintable: true`.
   *
   * Needed because the offer is now a server fact delivered on the submit
   * response, and **autoplay never submits** — so without this the hand-back
   * CTA is unphotographable by the Layer-3 harness by construction, which is
   * H01's lesson arriving a third time. Debug builds only, and it forces the
   * ANSWER, never the derivation: a real client still cannot show the verb
   * unless the server minted a slice.
   */
  handoffMint: boolean;
  /** `&handoff=1`: pretend this device won the `HANDOFF_PCT` draw (§4bis.31).
   *  Nested inside the duel arm exactly as the real flag is, and like `&duel=1`
   *  it forces the ARM, never the feature — the server still has to have
   *  derived a hand-back slice, because only it can. */
  handoff: boolean;
  /** `&duelhold=1`: hold the duel countdown on its first beat instead of
   *  counting in. A 1.8 s wall-clock animation has no deterministic frame to
   *  photograph, so the Layer-3 shot needs the overlay to stand still — the
   *  same reason `&pauseAt=` exists for the loop. */
  duelHold: boolean;
  /** `&sealhold=1`: hold the §4bis.30 mid-run 活过了 stamp at its fully
   *  punched-in state instead of letting it fade after 1.4 s. Same reason
   *  `&duelhold=1` exists — a wall-clock animation has no deterministic frame to
   *  photograph, and this one plays WHILE the run continues, so a capture that
   *  arrives 200 ms late finds an empty canvas and a green baseline that proves
   *  nothing. */
  sealHold: boolean;
}

export type SceneName = 'intro' | 'running' | 'paused' | 'dead' | 'result' | 'tutorial';

function intParam(q: URLSearchParams, key: string): number | null {
  const raw = q.get(key);
  if (raw === null || raw === '') return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

/** Returns null when the URL asks for nothing (the normal case, and always in a
 *  non-debug build). */
export function readDebugOptions(search: string): DebugOptions | null {
  if (!__BEATME_DEBUG__) return null;
  const q = new URLSearchParams(search);

  const tierRaw = q.get('autoplay');
  const autoplay =
    tierRaw !== null && TIER_NAMES.includes(tierRaw as BotTier) ? (tierRaw as BotTier) : null;
  const pauseAt = intParam(q, 'pauseAt');
  const timescale = intParam(q, 'timescale');
  const theme = q.get('theme');
  const result = q.get('result');
  const tutorial = q.get('tutorial');
  const ladder = q.get('ladder');
  const peak = q.get('peak');
  const peakui = q.get('peakui');
  const fair = q.get('fair');
  const duel = q.get('duel');
  const handoff = q.get('handoff');
  const handoffmint = q.get('handoffmint');
  const duelhold = q.get('duelhold');
  const sealhold = q.get('sealhold');

  if (
    autoplay === null &&
    pauseAt === null &&
    timescale === null &&
    theme === null &&
    result === null &&
    tutorial === null &&
    ladder === null &&
    peak === null &&
    peakui === null &&
    fair === null &&
    duel === null &&
    handoff === null &&
    handoffmint === null &&
    duelhold === null &&
    sealhold === null
  ) {
    return null;
  }

  return {
    autoplay,
    botSeed: intParam(q, 'botseed') ?? 1,
    pauseAt: pauseAt !== null && pauseAt >= 0 ? pauseAt : null,
    timescale: timescale !== null && timescale > 1 ? timescale : 1,
    showResult: result === '1',
    theme: theme !== null && theme !== '' ? theme : null,
    tutorial: tutorial === '1',
    ladder: ladder === '1',
    peak: peak === '1',
    showPeak: peakui === '1',
    fair: fair === '1',
    duel: duel === '1',
    handoff: handoff === '1',
    handoffMint: handoffmint === '1',
    duelHold: duelhold === '1',
    sealHold: sealhold === '1',
  };
}

/** The bot's input stream for a track, truncated where the bot actually died —
 *  exactly what a real client would have recorded. */
export function autoplayEvents(seed: number, tier: BotTier, botSeed: number): InputEvent[] {
  // Debug always previews the CURRENT gen version by design (§4bis.24) —
  // rendering an OLD track here would need its persisted sim_version instead.
  const obstacles = generateTrack(seed);
  return truncateAtDeath(seed, obstacles, botInputs(obstacles, { ...BOT_TIERS[tier], seed: botSeed }));
}

/** All Layer-2 run state. One object so one dead branch removes all of it. */
export class Playtest {
  private events: InputEvent[] = [];
  private cursor = 0;
  private frozen = false;
  private sceneName: SceneName = 'intro';

  constructor(readonly options: DebugOptions) {}

  /** The bot, not a thumb, drives this run. */
  get isAutoplay(): boolean {
    return this.options.autoplay !== null;
  }

  get showResult(): boolean {
    return this.options.showResult;
  }

  get timescale(): number {
    return this.options.timescale;
  }

  /** `&tutorial=1` — the harness's deterministic way to a stop frame. */
  get forceTutorial(): boolean {
    return this.options.tutorial;
  }

  /** `&ladder=1` — the only way to reach the ladder while `LADDER_PCT` is 0. */
  get forceLadder(): boolean {
    return this.options.ladder;
  }

  /** `&peakui=1` — autoplay may mount the seal (§4bis.22); mirrors showResult. */
  get showPeak(): boolean {
    return this.options.showPeak;
  }

  /** `&sealhold=1` — freeze the §4bis.30 mid-run stamp at full size, so a
   *  Layer-3 shot of a beat that plays DURING the run is deterministic. */
  get holdSeal(): boolean {
    return this.options.sealHold;
  }

  /** `&handoffmint=1` — stand in for the server's `handoffMintable: true`, so
   *  the hand-back CTA can be photographed on a path that never submits. */
  get forceHandoffMint(): boolean {
    return this.options.handoffMint;
  }

  /** `&duelhold=1` — hold the duel countdown for a deterministic shot. */
  get holdDuel(): boolean {
    return this.options.duelHold;
  }

  /** New run on `seed`: regenerate the bot stream, thaw. */
  reset(seed: number): void {
    const tier = this.options.autoplay;
    this.events = tier === null ? [] : autoplayEvents(seed, tier, this.options.botSeed);
    this.cursor = 0;
    this.frozen = false;
    this.sceneName = 'intro';
  }

  scene(name: SceneName): void {
    this.sceneName = name;
  }

  /**
   * §4bis.28: drop every bot event before `frame`. A duel fast-forwards both
   * Sims to the handover WITHOUT consuming autoplay input (the prefix is the
   * sender's stream, not the bot's), so without this the cursor would still sit
   * at 0 and the very first `drain` would dump ~20 frames of queued actions
   * into one frame — a bot mashing every button it ever meant to press.
   */
  skipTo(frame: number): void {
    while (this.cursor < this.events.length && (this.events[this.cursor]?.f ?? 0) < frame) {
      this.cursor++;
    }
  }

  /** Bot input for the frame about to be simulated — the same shape the
   *  InputController would have produced from a human's thumb. */
  drain(frame: number): Action[] {
    const out: Action[] = [];
    while (this.cursor < this.events.length) {
      const ev = this.events[this.cursor];
      if (!ev || ev.f > frame) break;
      out.push(ev.a);
      this.cursor++;
    }
    return out;
  }

  /** True exactly once, on the frame `&pauseAt=` names. */
  shouldFreeze(frame: number): boolean {
    const at = this.options.pauseAt;
    if (at === null || this.frozen || frame < at) return false;
    this.frozen = true;
    this.sceneName = 'paused';
    return true;
  }

  /** Autoplay death holds the death frame instead of opening the result flow. */
  freeze(): void {
    this.frozen = true;
  }

  /** `window.__beatme` — tooling polls this instead of guessing with timeouts. */
  expose(frame: () => number): void {
    exposeHandle({
      frame,
      scene: (): SceneName => this.sceneName,
      paused: (): boolean => this.frozen,
    });
  }

  /** Small HUD corner badge. Inline styles on purpose: the shipped stylesheet
   *  must not carry rules for a feature the shipped bundle cannot reach. */
  badge(root: HTMLElement): void {
    root.append(
      el('div', {
        text: 'DEMO',
        attrs: {
          style:
            'position:absolute;top:10px;left:10px;z-index:5;pointer-events:none;' +
            'padding:3px 9px;border-radius:999px;font:700 12px/1.4 system-ui,sans-serif;' +
            'letter-spacing:.14em;color:var(--bg,#fff);background:var(--ink,#1b1f24);opacity:.72',
        },
      }),
    );
  }
}

export function createPlaytest(options: DebugOptions | null): Playtest | null {
  if (!__BEATME_DEBUG__ || options === null) return null;
  return new Playtest(options);
}

/** Merges into `window.__beatme` rather than replacing it: the audio hook is
 *  installed at boot, the run handle only once a Session starts, and neither may
 *  clobber the other. */
function exposeHandle(patch: Record<string, unknown>): void {
  const w = window as unknown as { __beatme?: Record<string, unknown> };
  w.__beatme = { ...(w.__beatme ?? {}), ...patch };
}

/**
 * `window.__beatme.renderSfx(name)` → `{ sampleRate, channelData }`, one SFX
 * rendered offline (see game/audio.ts). Unlike autoplay this needs no URL param:
 * `tools/visual/src/audio.ts` just opens the page and calls it. `sfxNames` is
 * published alongside so the tool never hard-codes the roster.
 */
export function exposeAudioDebug(): void {
  if (!__BEATME_DEBUG__) return;
  exposeHandle({
    sfxNames: (): readonly Sfx[] => SFX_NAMES,
    renderSfx: (name: Sfx): Promise<RenderedSfx> => renderSfx(name),
  });
}
